import React, { useState, useRef, useEffect } from 'react'
import { useUsers } from '@/hooks/queries/useUsers'
import { useCreateUser, useUpdateUser, useDeleteUser, useMassDeleteUsers } from '@/hooks/mutations/users'
import { toast } from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import Pagination from './Pagination'
import useAuth from '@/hooks/stores/useAuth'
import api from '@/lib/axios'
import { userService } from '@/services/userService'
import { UserActions } from '@/components/UserActions'

const headers = [
  // { name: '', styles: 'align-top', inputType: 'text', searchIcon: true },

  { name: '', styles: 'align-top text-left', inputType: 'text', searchIcon: true },
  {
    name: 'Name',
    styles: 'min-w-[180px] text-left',
    inputType: 'text',
    searchIcon: false,
  },
  {
    name: 'Email',
    styles: 'min-w-[250px] text-left',
    inputType: 'text',
    searchIcon: false,
  },
  {
    name: 'Roles',
    styles: 'min-w-[250px] text-left',
    inputType: 'text',
    searchIcon: false,
  },
  {
    name: 'Status',
    styles: 'min-w-[120px] text-left',
    inputType: 'text',
    searchIcon: false,
  },
  { name: '', styles: 'min-w-[80px] text-left' },
  // {
  //   name: 'Name/ID',
  //   styles: 'min-w-[160px]',
  //   inputType: 'text',
  //   searchIcon: false,
  // },
  // {
  //   name: 'Position',
  //   styles: 'min-w-[130px]',
  //   inputType: 'text',
  //   arrowIcon: true,
  // },
  // {
  //   name: 'BDay',
  //   styles: 'min-w-[170px]',
  //   inputType: 'date',
  //   calendarIcon: true,
  // },
  // {
  //   name: 'Email/Phone',
  //   styles: 'min-w-[220px]',
  //   inputType: 'text',
  //   searchIcon: true,
  // },
  // {
  //   name: 'Address',
  //   styles: 'min-w-[220px]',
  //   inputType: 'text',
  //   arrowIcon: true,
  // },
  // {
  //   name: 'Status',
  //   styles: 'min-w-[120px]',
  //   inputType: 'text',
  //   arrowIcon: true,
  // },
  // { name: '', styles: 'min-w-[100px]' },
]

const Users = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchParams, setSearchParams] = useState({});
  const [paginationData, setPaginationData] = useState({
    current_page: 1,
    total: 0,
    per_page: 10,
    last_page: 1
  });
  const searchInputRef = useRef(null);
  const navigate = useNavigate();
  const { userRole } = useAuth(); // Get user role for conditional rendering

  // Update this line to destructure refetch from the query
  const { data: response, isLoading, isError, isFetching, refetch } = useUsers(currentPage, searchParams);

  // Handle input changes with debouncing for empty input
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // We'll debounce the empty string check to avoid immediate API calls on backspace
    if (value === '') {
      // Set a small timeout to debounce clearing
      setTimeout(() => {
        setDebouncedSearchTerm('');
      }, 300);
    } else {
      setDebouncedSearchTerm(value);
    }
  };
  
  // Effect to auto-clear search when input becomes empty
  useEffect(() => {
    if (debouncedSearchTerm === '' && Object.keys(searchParams).length > 0) {
      clearSearch();
    }
  }, [debouncedSearchTerm]);

  // Update handlePageChange to be simpler
  const handlePageChange = (pageNumber) => {
    console.log('Page change clicked:', pageNumber);
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  // Add a function to handle search submission
  const handleSearch = (e) => {
    e.preventDefault();
    
    // Create a new searchParams object based on the searchTerm
    const newParams = {};
    
    // Check if searchTerm contains @ which likely means it's an email
    if (searchTerm.includes('@')) {
      newParams.email = searchTerm;
    } else if (searchTerm.trim()) {
      newParams.name = searchTerm;
    }
    
    // Update search params state
    setSearchParams(newParams);
    
    // Reset to first page when searching
    setCurrentPage(1);
    
    // Maintain focus on the search input
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };
  
  // Add a function to clear the search
  const clearSearch = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setSearchParams({});
    setCurrentPage(1);
    
    // Maintain focus on the search input after clearing
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Update pagination data when response changes
  useEffect(() => {
    if (response) {
      setPaginationData({
        current_page: response.current_page,
        total: response.total,
        per_page: response.per_page,
        last_page: response.last_page
      });
    }
  }, [response]);

  // Filter users from the response data - no longer needed as API does the filtering
  const filteredUsers = response?.data || [];
  
  // Add the missing handleDelete function
  const deleteUser = useDeleteUser();
  
  const handleDelete = async (id) => {
    try {
      await deleteUser.mutateAsync(id);
      toast.success('User deleted successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to delete user');
    }
  };

  // Log the users data to the console
  console.log('Users data:', response);

  // Create loading indicator component to reuse
  const LoadingIndicator = () => (
    <div className="flex justify-center py-4">
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#F16745]"></div>
    </div>
  );

  // Remove full-page loader, we'll have inline loaders instead
  if (isError) return <div>Error loading users</div>;

  return (
    <div className="px-7 sm:px-9 lg:px-10 py-12 w-full mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#F16745] dark:text-[#F16745] mb-2">Users Management</h1>
          <p className="text-base text-body-color dark:text-dark-6">Manage and monitor user accounts</p>
        </div>
      </div>

      <div className="bg-white rounded-lg dark:bg-dark-2 shadow-card relative">
        {/* Search Section - always visible even during loading */}
        <div className="flex items-center justify-between flex-wrap sm:flex-nowrap gap-4 border-b border-stroke dark:border-dark-3 pt-[8px] pb-[18px]">
          <div className="w-full sm:w-auto sm:flex-1 max-w-full sm:max-w-[420px]">
            <form onSubmit={handleSearch}>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                  </svg>
                </div>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={handleSearchInputChange}
                  style={{ boxShadow: 'none' }}
                  className="h-12 w-full pl-10 pr-4 rounded-md border border-gray-300 bg-white text-gray-700 placeholder:text-gray-400 hover:border-gray-400 focus:border-[#F16745] focus:outline-none focus:ring-0 transition-colors"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="absolute right-[46px] top-0 h-full px-2 flex items-center justify-center text-gray-400 hover:text-gray-600"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
                <button 
                  type="submit"
                  className="absolute top-0 right-0 flex h-full w-[46px] items-center justify-center bg-[#F16745] text-white rounded-r-md"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17.2687 15.6657L12.6281 11.897C14.5406 9.28135 14.3437 5.54072 11.9531 3.17822C10.6875 1.9126 8.99995 1.20947 7.19995 1.20947C5.39995 1.20947 3.71245 1.9126 2.44683 3.17822C-0.168799 5.79385 -0.168799 10.0688 2.44683 12.6845C3.71245 13.9501 5.39995 14.6532 7.19995 14.6532C8.91558 14.6532 10.5187 14.0063 11.7843 12.8532L16.4812 16.6501C16.5937 16.7345 16.7343 16.7907 16.875 16.7907C17.0718 16.7907 17.2406 16.7063 17.3531 16.5657C17.5781 16.2845 17.55 15.8907 17.2687 15.6657ZM7.19995 13.3876C5.73745 13.3876 4.38745 12.8251 3.34683 11.7845C1.20933 9.64697 1.20933 6.1876 3.34683 4.07822C4.38745 3.0376 5.73745 2.4751 7.19995 2.4751C8.66245 2.4751 10.0125 3.0376 11.0531 4.07822C13.1906 6.21572 13.1906 9.6751 11.0531 11.7845C10.0406 12.8251 8.66245 13.3876 7.19995 13.3876Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </div>
            </form>
          </div>
          
          {userRole?.toLowerCase() === 'admin' && (
            <button 
              onClick={() => navigate('/users/create')}
              className="group relative inline-flex items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-[#F16745] to-[#F16745]/80 p-0.5 text-sm font-medium text-white hover:text-white focus:outline-none focus:ring-4 focus:ring-[#F16745]/50 whitespace-nowrap"
            >
              <span className="relative flex items-center gap-2 rounded-md bg-[#F16745] px-5 py-2.5 transition-all duration-75 ease-in group-hover:bg-opacity-0">
                <span className="whitespace-nowrap text-base">Add User</span>
              </span>
            </button>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-visible">
          <div className="overflow-x-auto overflow-y-visible">
            <table className="w-full table-auto">
              <TableHead 
                headers={headers} 
                selectedUsers={selectedUsers}
                setSelectedUsers={setSelectedUsers}
                users={response}
              />
              {isLoading ? (
                <tbody>
                  <tr>
                    <td colSpan={headers.length} className="py-8 text-center">
                      <LoadingIndicator />
                    </td>
                  </tr>
                </tbody>
              ) : filteredUsers && filteredUsers.length > 0 ? (
                <TableBody 
                  data={filteredUsers} 
                  selectedUsers={selectedUsers}
                  setSelectedUsers={setSelectedUsers}
                  refetch={refetch}
                />
              ) : (
                <tbody>
                  <tr>
                    <td colSpan={headers.length} className="text-center py-8 text-gray-500">
                      No users found
                    </td>
                  </tr>
                </tbody>
              )}
            </table>
          </div>
        </div>

        {/* Mobile/Tablet View */}
        <div className="lg:hidden space-y-4 p-4 overflow-visible">
          {isLoading ? (
            <LoadingIndicator />
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {filteredUsers && filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                <div key={user.id} className="bg-white p-4 rounded-lg shadow border border-stroke">
                  <div className="space-y-3 break-words">
                    <div className="flex justify-between items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <h3 className="text-[#F16745] font-medium truncate">{user.name}</h3>
                        <p className="text-sm mt-3 text-gray-600 break-all">{user.email}</p>
                      </div>
                      <div className="flex-shrink-0">
                        {user.is_deceased !== 1 && (
                          <UserActions 
                            userId={user.id}
                            onDelete={() => handleDelete(user.id)}
                            onStatusChange={async () => {
                              await refetch();
                            }}
                          />
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-600">Roles:</p>
                        <div className="flex flex-wrap gap-2">
                          {user.roles && Array.isArray(user.roles) ? (
                            user.roles.map((role, idx) => (
                              <span 
                                key={idx} 
                                className="bg-gray-100 dark:bg-dark-3 px-2 py-1 rounded text-sm break-all"
                              >
                                {role.title}
                              </span>
                            ))
                          ) : (
                            <span className="text-gray-500">No roles assigned</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 mt-2">
                        <p className="text-sm font-medium text-gray-600">Status:</p>
                        <div>
                          {user.is_deceased === 1 ? (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-[#F16745]">
                              <svg className="w-3 h-3 mr-1.5 text-[#F16745]" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"></path>
                              </svg>
                              Deceased
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[#F16745] text-white">
                              <svg className="w-3 h-3 mr-1.5 text-white" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                              </svg>
                              Active
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="pt-2">
                        <Checkbox 
                          index={user.id}
                          isChecked={selectedUsers.includes(user.id)}
                          onChange={(checked) => {
                            if (checked) {
                              setSelectedUsers([...selectedUsers, user.id])
                            } else {
                              setSelectedUsers(selectedUsers.filter(id => id !== user.id))
                            }
                          }}
                          label="Select User"
                        />
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-2 text-center text-gray-500 py-4">
                  No users found
                </div>
              )}
            </div>
          )}
        </div>
          
        {/* Pagination - don't hide during fetching, only during initial loading */}
        {filteredUsers.length > 0 && !isLoading && (
          <div className="w-full border-t border-stroke">
            <Pagination
              currentPage={paginationData.current_page}
              totalPages={paginationData.last_page}
              onPageChange={handlePageChange}
              itemsPerPage={paginationData.per_page}
              totalItems={paginationData.total}
            />
          </div>
        )}
      </div>
    </div>
  )
}

const TableTop = ({ searchTerm, setSearchTerm, onSearch, onClear }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const createUser = useCreateUser();
  const navigate = useNavigate();
  const searchInputRef = useRef(null);

  const handleCreateUser = async (userData) => {
    // Ensure userData only contains name, email, password, and role
    const { name, email, password, roles } = userData;
    const newUserData = { name, email, password, roles };

    try {
      await createUser.mutateAsync(newUserData);
      toast.success('User created successfully');
      setShowCreateModal(false);
    } catch (error) {
      console.log('Error response:', error.response); // Debugging line
      if (error.response?.status === 422 && error.response?.data?.message === "The email has already been taken.") {
        toast.error('Email already exists');
      } else {
        toast.error(error.message || 'Failed to create user');
      }
    }
  }

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Debounce empty string
    if (value === '') {
      setTimeout(() => {
        setDebouncedSearchTerm('');
      }, 300);
    } else {
      setDebouncedSearchTerm(value);
    }
  };
  
  // Effect to auto-clear search when input becomes empty
  useEffect(() => {
    if (debouncedSearchTerm === '' && searchTerm === '') {
      onClear && onClear();
    }
  }, [debouncedSearchTerm, searchTerm, onClear]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSearch && onSearch(e);
    
    // Keep focus on input after search
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }

  const handleClear = () => {
    onClear && onClear();
    
    // Keep focus on input after clearing
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }

  return (
    <div className='items-center justify-between space-y-4 border-b border-stroke dark:border-dark-3 py-[18px] px-4 sm:px-5 sm:flex sm:space-x-4 sm:space-y-0 mt-2'>
      <div className='w-full md:max-w-[420px]'>
        <form onSubmit={handleSubmit}>
          <div className='relative'>
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg className="w-5 h-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            </div>
            <input
              ref={searchInputRef}
              type='text'
              placeholder='Search by name or email...'
              value={searchTerm}
              style={{ boxShadow: 'none' }}
              onChange={handleInputChange}
              className='h-12 w-full pl-10 pr-4 rounded-md border border-gray-300 bg-white text-gray-700 placeholder:text-gray-400 hover:border-gray-400 focus:border-[#F16745] focus:outline-none focus:ring-0 transition-colors'
            />
            {searchTerm && (
              <button
                type="button"
                onClick={handleClear}
                className="absolute right-[46px] top-0 h-full px-2 flex items-center justify-center text-gray-400 hover:text-gray-600"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            <button 
              type="submit"
              className='absolute top-0 right-0 flex h-full w-[46px] items-center justify-center bg-[#F16745] text-white rounded-r-md'>
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17.2687 15.6657L12.6281 11.897C14.5406 9.28135 14.3437 5.54072 11.9531 3.17822C10.6875 1.9126 8.99995 1.20947 7.19995 1.20947C5.39995 1.20947 3.71245 1.9126 2.44683 3.17822C-0.168799 5.79385 -0.168799 10.0688 2.44683 12.6845C3.71245 13.9501 5.39995 14.6532 7.19995 14.6532C8.91558 14.6532 10.5187 14.0063 11.7843 12.8532L16.4812 16.6501C16.5937 16.7345 16.7343 16.7907 16.875 16.7907C17.0718 16.7907 17.2406 16.7063 17.3531 16.5657C17.5781 16.2845 17.55 15.8907 17.2687 15.6657ZM7.19995 13.3876C5.73745 13.3876 4.38745 12.8251 3.34683 11.7845C1.20933 9.64697 1.20933 6.1876 3.34683 4.07822C4.38745 3.0376 5.73745 2.4751 7.19995 2.4751C8.66245 2.4751 10.0125 3.0376 11.0531 4.07822C13.1906 6.21572 13.1906 9.6751 11.0531 11.7845C10.0406 12.8251 8.66245 13.3876 7.19995 13.3876Z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
        </form>
      </div>
      <div className='w-full sm:w-auto'>
        {userRole === 'admin' && (
          <button 
            onClick={() => navigate('/users/create')}
            className='w-full sm:w-auto flex h-[46px] items-center justify-center whitespace-nowrap rounded-md bg-[#F16745] px-6 text-base font-medium text-white hover:bg-opacity-90'
          >
            <span className='mr-2'>Add User</span>
          </button>
        )}
      </div>
    </div>
  )
}

const TableHead = ({ headers, selectedUsers, setSelectedUsers, users }) => {
  const checkboxId = 'checkboxId'

  return (
    <thead>
      <tr className='border-b border-stroke dark:border-dark-3'>
        {headers.map((header, index) => (
          <th
            className={`px-4 pt-9 pb-6 first:pl-8 align-top ${header.styles}`}
            key={index}
          >
            {index === 0 && users?.data?.length > 0 && (
              <Checkbox 
                index={checkboxId} 
                isChecked={users?.data?.length > 0 && selectedUsers.length === users.data.length}
                onChange={(checked) => {
                  if (checked) {
                    setSelectedUsers(users.data.map(user => user.id))
                  } else {
                    setSelectedUsers([])
                  }
                }} 
              />
            )}
            {index !== 0 && index !== headers.length - 1 && (
              <div>
                <div className='mb-[10px] flex items-center'>
                  <p className='mr-1 text-base font-medium text-body-color dark:text-dark-6'>
                    {header.name}
                  </p>
                  {/* <div className='inline-flex flex-col space-y-[2px] text-body-color dark:text-dark-6'>
                    <span className='inline-block'>
                      <svg
                        width='10'
                        height='5'
                        viewBox='0 0 10 5'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path d='M5 0L0 5H10L5 0Z' fill='currentColor' />
                      </svg>
                    </span>
                    <span className='inline-block'>
                      <svg
                        width='10'
                        height='5'
                        viewBox='0 0 10 5'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path
                          d='M5 5L10 0L-4.37114e-07 8.74228e-07L5 5Z'
                          fill='currentColor'
                        />
                      </svg>
                    </span>
                  </div> */}
                </div>
                <div className='relative'>
                  {/* <input
                    type={header.inputType}
                    className='h-[34px] w-full rounded border border-stroke pr-3 pl-8 text-sm text-body-color outline-none dark:border-dark-3 dark:text-dark-6 bg-transparent'
                  /> */}
                  {header.searchIcon && (
                    <span className='absolute -translate-y-1/2 top-1/2 left-3 text-body-color dark:text-dark-6'>
                      <svg
                        width={14}
                        height={14}
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M6.41699 2.33268C4.16183 2.33268 2.33366 4.16085 2.33366 6.41601C2.33366 8.67118 4.16183 10.4993 6.41699 10.4993C8.67215 10.4993 10.5003 8.67118 10.5003 6.41601C10.5003 4.16085 8.67215 2.33268 6.41699 2.33268ZM1.16699 6.41601C1.16699 3.51652 3.5175 1.16602 6.41699 1.16602C9.31649 1.16602 11.667 3.51652 11.667 6.41601C11.667 9.31551 9.31649 11.666 6.41699 11.666C3.5175 11.666 1.16699 9.31551 1.16699 6.41601Z"
                          fill="currentColor"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M9.29976 9.29976C9.52757 9.07195 9.89691 9.07195 10.1247 9.29976L12.6622 11.8373C12.89 12.0651 12.89 12.4344 12.6622 12.6622C12.4344 12.89 12.0651 12.89 11.8373 12.6622L9.29976 10.1247C9.07195 9.89691 9.07195 9.52757 9.29976 9.29976Z"
                          fill="currentColor"
                        />
                      </svg>
                    </span>
                  )}
                  {header.arrowIcon && (
                    <span className='absolute -translate-y-1/2 text-body-color dark:text-dark-6 right-3 top-1/2'>
                      <svg
                        width={10}
                        height={5}
                        viewBox="0 0 10 5"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M5 5L10 0L-4.37114e-07 8.74228e-07L5 5Z" fill="currentColor" />
                      </svg>
                    </span>
                  )}
                  {header.calendarIcon && (
                    <span className='absolute -translate-y-1/2 text-body-color dark:text-dark-6 top-1/2 left-3'>
                      <svg
                        width={14}
                        height={14}
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M4.66699 0.583984C4.98916 0.583984 5.25033 0.845152 5.25033 1.16732V1.75065H8.75033V1.16732C8.75033 0.845152 9.01149 0.583984 9.33366 0.583984C9.65582 0.583984 9.91699 0.845152 9.91699 1.16732V1.75065H11.0837C12.0502 1.75065 12.8337 2.53415 12.8337 3.50065V11.6673C12.8337 12.6338 12.0502 13.4173 11.0837 13.4173H2.91699C1.95049 13.4173 1.16699 12.6338 1.16699 11.6673V3.50065C1.16699 2.53415 1.95049 1.75065 2.91699 1.75065H4.08366V1.16732C4.08366 0.845152 4.34483 0.583984 4.66699 0.583984ZM4.08366 2.91732H2.91699C2.59483 2.91732 2.33366 3.17848 2.33366 3.50065V5.25065H11.667V3.50065C11.667 3.17848 11.4058 2.91732 11.0837 2.91732H9.91699V3.50065C9.91699 3.82282 9.65582 4.08398 9.33366 4.08398C9.01149 4.08398 8.75033 3.82282 8.75033 3.50065V2.91732H5.25033V3.50065C5.25033 3.82282 4.98916 4.08398 4.66699 4.08398C4.34483 4.08398 4.08366 3.82282 4.08366 3.50065V2.91732ZM11.667 6.41732H2.33366V11.6673C2.33366 11.9895 2.59483 12.2507 2.91699 12.2507H11.0837C11.4058 12.2507 11.667 11.9895 11.667 11.6673V6.41732Z"
                          fill="currentColor"
                        />
                      </svg>
                    </span>
                  )}
                </div>
              </div>
            )}
            {index === headers.length - 1 && null}
          </th>
        ))}
      </tr>
    </thead>
  )
}

const TableBody = ({ data, selectedUsers, setSelectedUsers, refetch }) => {
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();

  const handleDelete = async (id) => {
    try {
      await deleteUser.mutateAsync(id);
      toast.success('User deleted successfully');
      await refetch();
    } catch (error) {
      toast.error(error.message || 'Failed to delete user');
    }
  };

  const handleUpdate = async (id, userData) => {
    try {
      await updateUser.mutateAsync({ id, data: userData });
      toast.success('User updated successfully');
      await refetch();
    } catch (error) {
      toast.error(error.message || 'Failed to update user');
    }
  };

  return (
    <tbody>
      {data?.map((row, index) => (
        <tr
          className="border-b border-stroke dark:border-dark-3 hover:bg-[#F16745]/5"
          key={row.id}
        >
          <td className='py-5 pl-8'>
            <Checkbox 
              index={row.id}
              isChecked={selectedUsers.includes(row.id)}
              onChange={(checked) => {
                if (checked) {
                  setSelectedUsers([...selectedUsers, row.id])
                } else {
                  setSelectedUsers(selectedUsers.filter(id => id !== row.id))
                }
              }}
            />
          </td>
          <td className='py-5 px-4'>
            <div className="flex items-center">
              <p className="text-base font-medium text-[#F16745]">
                {row.name}
              </p>
            </div>
          </td>
          <td className='py-5 px-4'>
            <p className="text-base text-body-color dark:text-dark-6">
              {row.email}
            </p>
          </td>
          <td className='py-5 px-4'>
            <p className="text-base text-body-color dark:text-dark-6">  
              {row.roles && Array.isArray(row.roles)
                ? row.roles.map(role => role.title).join(", ")
                : "No roles"}
            </p>
          </td>
          <td className='py-5 px-4'>
            {row.is_deceased === 1 ? (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-[#F16745]">
                <svg className="w-3 h-3 mr-1.5 text-[#F16745]" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"></path>
                </svg>
                Deceased
              </span>
            ) : (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[#F16745] text-white">
                <svg className="w-3 h-3 mr-1.5 text-white" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                </svg>
                Active
              </span>
            )}
          </td>
          <td className='py-5 pl-4 pr-8'>
            <UserActions 
              userId={row.id}
              onDelete={() => handleDelete(row.id)}
              onStatusChange={async () => {
                await refetch();
              }}
            />
          </td>
        </tr>
      ))}
    </tbody>
  )
}

const Checkbox = ({ index, isChecked, onChange, label }) => {
  return (
    <div className='relative mt-1 flex items-center gap-2'>
      <input
        type='checkbox'
        name='tableCheckbox'
        id={index}
        checked={isChecked}
        onChange={(e) => onChange(e.target.checked)}
        className='tableCheckbox sr-only'
      />
      <label
        htmlFor={index}
        className={`flex h-[18px] w-[18px] cursor-pointer items-center justify-center rounded border ${
          isChecked ? '!bg-[#F16745] border-[#F16745]' : 'border-stroke dark:border-dark-4'
        }`}
      >
        <span className={`icon ${isChecked ? '' : 'opacity-0'} text-white`}>
          <svg
            width='14'
            height='14'
            viewBox='0 0 14 14'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              fillRule='evenodd'
              clipRule='evenodd'
              d='M12.0791 3.08687C12.307 3.31468 12.307 3.68402 12.0791 3.91183L5.66248 10.3285C5.43467 10.5563 5.06533 10.5563 4.83752 10.3285L1.92085 7.41183C1.69305 7.18402 1.69305 6.81468 1.92085 6.58687C2.14866 6.35906 2.51801 6.35906 2.74581 6.58687L5.25 9.09106L11.2542 3.08687C11.482 2.85906 11.8513 2.85906 12.0791 3.08687Z'
              fill='currentColor'
            />
          </svg>
        </span>
      </label>
      {label && (
        <span className='text-sm text-gray-600'>{label}</span>
      )}
    </div>
  )
}

export default Users
